import { DataFlow, reply } from './action.ts';
import { dispatch } from './dispatch.ts';
import {
  authorize,
  changeRepeatMode,
  changeVolume,
  clearQueue,
  getAlbumMedia,
  getAritstMedia,
  getCatalogArtistsIdView,
  getCurrentPlayerStatus,
  getMeLibraryAlbums,
  getMeLibraryArtist,
  getMeLibraryArtists,
  getMeLibraryPlaylists,
  getMeLibraryRecentlyAdded,
  getMeLibrarySongs,
  getPlaylistMedia,
  getRecentlyPlayed,
  getRecommendations,
  getRecommendationsId,
  getRecommendGroups,
  getResourceRelationship,
  initializeMusicKit,
  mute,
  pause,
  play,
  playLater,
  playNext,
  playTo,
  playToMediaItem,
  playTrackAt,
  requestAuthSigning,
  requestAuthToken,
  restore,
  search,
  seekToTime,
  skipToNextItem,
  skipToPreviousItem,
  stop,
  toggleShuffleMode,
  unauthorize,
  unmute,
} from './playerActions.ts';
import {
  CommandPayload,
  CommandPayloadType,
  NotificationPayload,
  NotificationPayloadType,
} from './types.ts';
import { L } from './utils.ts';

export const loadPlayer = () => {
  L.info(`Loading player...`);
};

const handleN2H2NMessage = async (
  messageId: string,
  payload: CommandPayload,
) => {
  switch (payload.type) {
    case CommandPayloadType.AuthSigning: {
      dispatch(reply(messageId, await requestAuthSigning(payload.value)));
      break;
    }
    case CommandPayloadType.AuthToken: {
      dispatch(reply(messageId, await requestAuthToken(payload.value)));
      break;
    }
    case CommandPayloadType.Play: {
      dispatch(reply(messageId, await play()));
      break;
    }
    case CommandPayloadType.Pause: {
      dispatch(reply(messageId, await pause()));
      break;
    }
    case CommandPayloadType.Stop: {
      dispatch(reply(messageId, await stop()));
      break;
    }
    case CommandPayloadType.SkipToNextItem: {
      dispatch(reply(messageId, await skipToNextItem()));
      break;
    }
    case CommandPayloadType.SkipToPreviousItem: {
      dispatch(reply(messageId, await skipToPreviousItem()));
      break;
    }
    case CommandPayloadType.SeekToTime: {
      dispatch(reply(messageId, await seekToTime(payload.value)));
      break;
    }
    case CommandPayloadType.PlayTo: {
      dispatch(reply(messageId, await playTo(payload.value)));
      break;
    }
    case CommandPayloadType.PlayToMediaItem: {
      dispatch(reply(messageId, await playToMediaItem(payload.value)));
      break;
    }
    case CommandPayloadType.PlayTrackAt: {
      dispatch(reply(messageId, await playTrackAt(payload.value)));
      break;
    }
    case CommandPayloadType.PlayNext: {
      dispatch(reply(messageId, await playNext(payload.value)));
      break;
    }
    case CommandPayloadType.PlayLater: {
      dispatch(reply(messageId, await playLater(payload.value)));
      break;
    }
    case CommandPayloadType.ClearQueue: {
      dispatch(reply(messageId, await clearQueue()));
      break;
    }
    case CommandPayloadType.CurrentPlayerStatus: {
      const result = await getCurrentPlayerStatus();
      dispatch(reply(messageId, result));
      break;
    }
    case CommandPayloadType.ChangeRepeatMode: {
      dispatch(reply(messageId, await changeRepeatMode(payload.value)));
      break;
    }
    case CommandPayloadType.ChangeVolume: {
      dispatch(reply(messageId, await changeVolume(payload.value)));
      break;
    }
    case CommandPayloadType.Mute: {
      dispatch(reply(messageId, await mute()));
      break;
    }
    case CommandPayloadType.Unmute: {
      dispatch(reply(messageId, await unmute()));
      break;
    }
    case CommandPayloadType.ToggleShuffleMode: {
      dispatch(reply(messageId, await toggleShuffleMode()));
      break;
    }
    case CommandPayloadType.Restore: {
      dispatch(reply(messageId, await restore(payload.value)));
      break;
    }
    case CommandPayloadType.GetRecommendations: {
      dispatch(reply(messageId, await getRecommendations()));
      break;
    }
    case CommandPayloadType.GetRecommendGroups: {
      dispatch(reply(messageId, await getRecommendGroups(payload.value)));
      break;
    }
    case CommandPayloadType.GetRecommendation: {
      dispatch(reply(messageId, await getRecommendationsId(payload.value)));
      break;
    }
    case CommandPayloadType.GetRecentlyPlayed: {
      dispatch(
        reply(messageId, await getRecentlyPlayed(payload.value)),
      );
      break;
    }
    case CommandPayloadType.GetMeLibraryPlaylists: {
      dispatch(reply(messageId, await getMeLibraryPlaylists(payload.value)));
      break;
    }
    case CommandPayloadType.GetMeLibraryRecentlyAdded: {
      dispatch(
        reply(messageId, await getMeLibraryRecentlyAdded(payload.value)),
      );
      break;
    }
    case CommandPayloadType.GetPlaylistMedia: {
      dispatch(
        reply(messageId, await getPlaylistMedia(payload.value)),
      );
      break;
    }
    case CommandPayloadType.GetMeLibraryAlbums: {
      dispatch(
        reply(messageId, await getMeLibraryAlbums(payload.value)),
      );
      break;
    }
    case CommandPayloadType.GetAlbumMedia: {
      dispatch(
        reply(messageId, await getAlbumMedia(payload.value)),
      );
      break;
    }
    case CommandPayloadType.GetMeLibraryArtists: {
      dispatch(
        reply(messageId, await getMeLibraryArtists(payload.value)),
      );
      break;
    }
    case CommandPayloadType.GetMeLibraryArtist: {
      dispatch(
        reply(messageId, await getMeLibraryArtist(payload.value)),
      );
      break;
    }
    case CommandPayloadType.GetMeLibrarySongs: {
      dispatch(
        reply(messageId, await getMeLibrarySongs(payload.value)),
      );
      break;
    }
    case CommandPayloadType.GetArtistMedia: {
      dispatch(
        reply(messageId, await getAritstMedia(payload.value)),
      );
      break;
    }
    case CommandPayloadType.GetCatalogArtistsIdView: {
      dispatch(
        reply(messageId, await getCatalogArtistsIdView(payload.value)),
      );
      break;
    }
    case CommandPayloadType.GetResourceRelationship: {
      dispatch(
        reply(messageId, await getResourceRelationship(payload.value)),
      );
      break;
    }
    case CommandPayloadType.Search: {
      dispatch(
        reply(messageId, await search(payload.value)),
      );
      break;
    }
    default:
      break;
  }
};

const handleN2HMessage = async (
  messageId: string,
  payload: NotificationPayload,
) => {
  L.log('[N2H]', messageId, JSON.stringify(payload));
  const type = payload.type;
  switch (type) {
    case NotificationPayloadType.NotifyDeveloperToken: {
      await initializeMusicKit(payload.value);
      break;
    }
    case NotificationPayloadType.Authorize: {
      await authorize();
      break;
    }
    case NotificationPayloadType.Unauthorize: {
      await unauthorize();
      break;
    }
    default:
      L.warn('Unknown payload type:', type);
      break;
  }
};

export const handlePortMessage = async (event: MessageEvent) => {
  L.log('Received message from native port:', event.data);

  if (!event.data) {
    L.warn('Received empty message from native port');
    return;
  }

  try {
    const data = JSON.parse(event.data);
    const { messageId, dataFlow, payload } = data;

    if (!payload.type) {
      throw new Error('Payload type is missing.');
    }

    if (dataFlow === DataFlow.N2H2N) {
      handleN2H2NMessage(messageId, payload);
    } else if (dataFlow === DataFlow.N2H) {
      await handleN2HMessage(messageId, payload);
    }
  } catch (err) {
    console.error(err);
  }
};
